// ไฟล์: frontend/src/pages/teacher/Savings.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { DEFAULT_AVATAR } from '../../assets/default_avatar';

function TeacherSavings() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [classSummary, setClassSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [formData, setFormData] = useState({
    student_id: '',
    transaction_date: new Date().toISOString().split('T')[0],
    amount: '',
    transaction_type: 'deposit',
    description: ''
  });
  
  useEffect(() => {
    fetchClasses();
  }, []);
  
  useEffect(() => {
    if (selectedClassId) {
      fetchStudentsByClass();
      fetchSavingsSummary();
    }
  }, [selectedClassId]);
  
  useEffect(() => {
    if (selectedClassId && selectedDate) {
      fetchSavingsByDate();
    }
  }, [selectedClassId, selectedDate]);
  
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentsByClass = async () => {
    try {
      const response = await api.student.getStudentsByClass(selectedClassId);
      // ไม่ต้องตั้งค่า students ตรงนี้ จะใช้ข้อมูลจาก fetchSavingsByDate แทน
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };
  
  const fetchSavingsSummary = async () => {
    try {
      const response = await api.savings.getSavingsSummaryByClass(selectedClassId);
      setClassSummary(response.data.data);
    } catch (error) {
      console.error('Error fetching savings summary:', error);
    }
  };
  
  const fetchSavingsByDate = async () => {
    try {
      setIsLoading(true);
      const response = await api.savings.getSavingsByClassAndDate(selectedClassId, selectedDate);
      setStudents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching savings by date:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลการออมเงินได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchStudentHistory = async (studentId) => {
    try {
      setIsLoading(true);
      const response = await api.savings.getStudentSavingsHistory(studentId);
      setTransactions(response.data.data.transactions || []);
      setBalance(response.data.data.balance || 0);
    } catch (error) {
      console.error('Error fetching student history:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดประวัติการออมเงินได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleShowTransactionModal = (student) => {
    setSelectedStudent(student);
    setFormData({
      student_id: student.student_id,
      transaction_date: selectedDate,
      amount: '',
      transaction_type: 'deposit',
      description: ''
    });
    setShowModal(true);
  };
  
  const handleShowHistoryModal = (student) => {
    setSelectedStudent(student);
    fetchStudentHistory(student.student_id);
    setShowHistoryModal(true);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบข้อมูลที่จำเป็น
    if (!formData.student_id || !formData.transaction_date || !formData.amount || !formData.transaction_type) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
        text: 'กรุณากรอกรหัสนักเรียน วันที่ จำนวนเงิน และประเภทรายการ',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    // ตรวจสอบจำนวนเงิน
    if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'จำนวนเงินไม่ถูกต้อง',
        text: 'กรุณากรอกจำนวนเงินที่มากกว่า 0',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // ส่งคำขอไปยัง API
      const response = await api.savings.recordTransaction(formData);
      
      Swal.fire({
        icon: 'success',
        title: 'บันทึกสำเร็จ',
        text: formData.transaction_type === 'deposit' ? 'บันทึกการฝากเงินเรียบร้อยแล้ว' : 'บันทึกการถอนเงินเรียบร้อยแล้ว',
        confirmButtonColor: '#ff6b01'
      });
      
      // ปิด Modal และโหลดข้อมูลใหม่
      setShowModal(false);
      fetchSavingsByDate();
      fetchSavingsSummary();
    } catch (error) {
      console.error('Error recording transaction:', error);
      let errorMessage = 'ไม่สามารถบันทึกรายการได้';
      
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: errorMessage,
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteTransaction = (id) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณแน่ใจหรือไม่ว่าต้องการลบรายการนี้?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ff6b01',
      cancelButtonColor: '#d33',
      confirmButtonText: 'ใช่, ลบรายการ',
      cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.savings.deleteTransaction(id);
          
          Swal.fire({
            icon: 'success',
            title: 'ลบรายการสำเร็จ',
            text: 'ลบรายการเรียบร้อยแล้ว',
            confirmButtonColor: '#ff6b01'
          });
          
          // โหลดข้อมูลใหม่
          fetchStudentHistory(selectedStudent.student_id);
          fetchSavingsByDate();
          fetchSavingsSummary();
        } catch (error) {
          console.error('Error deleting transaction:', error);
          Swal.fire({
            icon: 'error',
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถลบรายการได้',
            confirmButtonColor: '#ff6b01'
          });
        }
      }
    });
  };
  
  // แปลงเงินเป็นรูปแบบไทย
  const formatMoney = (amount) => {
    return parseFloat(amount).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // แปลงวันที่เป็นรูปแบบไทย
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('th-TH', options);
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">การออมเงินนักเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">การออมเงินนักเรียน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกชั้นเรียนและวันที่ */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">เลือกชั้นเรียนและวันที่</h3>
                      </div>
                      <div className="card-body">
                        {classes.length === 0 ? (
                          <div className="alert alert-warning">
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                          </div>
                        ) : (
                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>ชั้นเรียน</label>
                                <select
                                  className="form-control"
                                  value={selectedClassId}
                                  onChange={(e) => setSelectedClassId(e.target.value)}
                                >
                                  {classes.map(classItem => (
                                    <option key={classItem.id} value={classItem.id}>
                                      {classItem.class_name} (ห้อง {classItem.room_number})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>วันที่</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={selectedDate}
                                  onChange={(e) => setSelectedDate(e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* สรุปการออมเงิน */}
                {classSummary && (
                  <div className="row">
                    <div className="col-12">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปการออมเงินทั้งชั้นเรียน</h3>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-lg-3 col-md-6">
                              <div className="small-box bg-info">
                                <div className="inner">
                                  <h3>{formatMoney(classSummary.stats.total_balance)}</h3>
                                  <p>ยอดเงินออมรวม (บาท)</p>
                                </div>
                                <div className="icon">
                                  <i className="fas fa-money-bill-wave"></i>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <div className="small-box bg-success">
                                <div className="inner">
                                  <h3>{formatMoney(classSummary.stats.average_balance)}</h3>
                                  <p>ค่าเฉลี่ยต่อคน (บาท)</p>
                                </div>
                                <div className="icon">
                                  <i className="fas fa-calculator"></i>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <div className="small-box bg-warning">
                                <div className="inner">
                                  <h3>{formatMoney(classSummary.stats.max_balance)}</h3>
                                  <p>ยอดเงินสูงสุด (บาท)</p>
                                </div>
                                <div className="icon">
                                  <i className="fas fa-arrow-up"></i>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                              <div className="small-box bg-danger">
                                <div className="inner">
                                  <h3>{formatMoney(classSummary.stats.min_balance)}</h3>
                                  <p>ยอดเงินต่ำสุด (บาท)</p>
                                </div>
                                <div className="icon">
                                  <i className="fas fa-arrow-down"></i>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* รายการออมเงินของนักเรียนในวันนี้ */}
                <div className="row">
                  <div className="col-12">
                    <div className="card card-orange">
                      <div className="card-header">
                        <h3 className="card-title">รายการออมเงินประจำวันที่ {formatDate(selectedDate)}</h3>
                      </div>
                      <div className="card-body">
                        {students.length === 0 ? (
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle mr-2"></i>
                            ไม่พบข้อมูลนักเรียนในชั้นเรียนนี้
                          </div>
                        ) : (
                          <div className="row">
                            {students.map(student => (
                              <div key={student.student_id} className="col-md-4 col-sm-6 col-12 mb-3">
                                <div className="card card-outline card-orange">
                                  <div className="card-header">
                                    <h3 className="card-title">
                                      <img
                                        src={`https://cms2568.backend.kruwirat.com/uploads/${student.profile_pic}`}
                                        alt={`${student.first_name} ${student.last_name}`}
                                        className="img-circle mr-2"
                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                              onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = DEFAULT_AVATAR;
                                      }}
                                      />
                                      {student.first_name} {student.last_name}
                                    </h3>
                                  </div>
                                  <div className="card-body p-2">
                                    <div className="row mb-2">
                                      <div className="col-6 text-center">
                                        <div className="bg-success rounded p-1">
                                          <span className="text-white">ฝาก: {formatMoney(student.deposit_today)} บาท</span>
                                        </div>
                                      </div>
                                      <div className="col-6 text-center">
                                        <div className="bg-danger rounded p-1">
                                          <span className="text-white">ถอน: {formatMoney(student.withdraw_today)} บาท</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-center mb-2">
                                      <span className={`badge ${student.net_today >= 0 ? 'bg-success' : 'bg-danger'} p-2`} style={{ fontSize: '1rem' }}>
                                        สุทธิ: {formatMoney(student.net_today)} บาท
                                      </span>
                                    </div>
                                    <div className="text-center mt-3">
                                      <button
                                        className="btn btn-info btn-sm mr-2"
                                        onClick={() => handleShowHistoryModal(student)}
                                      >
                                        <i className="fas fa-history mr-1"></i> ประวัติ
                                      </button>
                                      <button
                                        className="btn btn-orange btn-sm"
                                        onClick={() => handleShowTransactionModal(student)}
                                      >
                                        <i className="fas fa-plus-circle mr-1"></i> ทำรายการ
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* ตารางสรุปยอดเงินออมทั้งชั้นเรียน */}
                {classSummary && (
                  <div className="row">
                    <div className="col-12">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">สรุปยอดเงินออมของนักเรียนทั้งชั้น</h3>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  <th style={{ width: '40px' }}>ลำดับ</th>
                                  <th style={{ width: '80px' }}>รหัส</th>
                                  <th>ชื่อ-นามสกุล</th>
                                  <th style={{ width: '150px' }}>ยอดเงินออม (บาท)</th>
                                  <th style={{ width: '120px' }}>ตัวเลือก</th>
                                </tr>
                              </thead>
                              <tbody>
                                {classSummary.students.map((student, index) => (
                                  <tr key={student.student_id}>
                                    <td>{index + 1}</td>
                                    <td>{student.student_code}</td>
                                    <td>
                                      <img
                                        src={`https://cms2568.backend.kruwirat.com/uploads/${student.profile_pic}`}
                                        alt={`${student.first_name} ${student.last_name}`}
                                        className="img-circle mr-2"
                                        style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = DEFAULT_AVATAR;
                                        }}
                                      />
                                      {student.first_name} {student.last_name}
                                    </td>
                                    <td className="text-right">{formatMoney(student.balance)}</td>
                                    <td>
                                      <button
                                        className="btn btn-info btn-sm mr-1"
                                        onClick={() => handleShowHistoryModal(student)}
                                      >
                                        <i className="fas fa-history"></i>
                                      </button>
                                      <button
                                        className="btn btn-orange btn-sm"
                                        onClick={() => handleShowTransactionModal(student)}
                                      >
                                        <i className="fas fa-plus-circle"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-light">
                                  <th colSpan="3" className="text-right">รวมทั้งสิ้น</th>
                                  <th className="text-right">{formatMoney(classSummary.stats.total_balance)}</th>
                                  <th></th>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      
      {/* Modal ทำรายการออมเงิน */}
      {showModal && selectedStudent && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  ทำรายการออมเงิน - {selectedStudent.first_name} {selectedStudent.last_name}
                </h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>ประเภทรายการ</label>
                    <select
                      className="form-control"
                      name="transaction_type"
                      value={formData.transaction_type}
                      onChange={handleInputChange}
                    >
                      <option value="deposit">ฝากเงิน</option>
                      <option value="withdraw">ถอนเงิน</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>วันที่</label>
                    <input
                      type="date"
                      className="form-control"
                      name="transaction_date"
                      value={formData.transaction_date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>จำนวนเงิน (บาท)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      className="form-control"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>หมายเหตุ</label>
                    <input
                      type="text"
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="(ไม่จำเป็นต้องระบุ)"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    ยกเลิก
                  </button>
                  <button type="submit" className="btn btn-orange" disabled={isLoading}>
                    {isLoading ? (
                      <><i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...</>
                    ) : (
                      <>บันทึก</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* พื้นหลังทึบ */}
          <div className="modal- fade show"></div>
        </div>
      )}
      
      {/* Modal แสดงประวัติการออมเงิน */}
      {showHistoryModal && selectedStudent && (
        <div className="modal fade show" style={{ display: 'block', paddingRight: '15px' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-orange">
                <h5 className="modal-title">
                  ประวัติการออมเงิน - {selectedStudent.first_name} {selectedStudent.last_name}
                </h5>
                <button type="button" className="close" onClick={handleCloseHistoryModal}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <h5><i className="icon fas fa-info"></i> ยอดเงินคงเหลือ</h5>
                  <h3 className="mb-0">{formatMoney(balance)} บาท</h3>
                </div>
                
                {isLoading ? (
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    ไม่พบประวัติการออมเงิน
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>ลำดับ</th>
                          <th>วันที่</th>
                          <th>ประเภท</th>
                          <th>จำนวนเงิน (บาท)</th>
                          <th>หมายเหตุ</th>
                          <th>บันทึกโดย</th>
                          <th style={{ width: '80px' }}>ตัวเลือก</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction, index) => (
                          <tr key={transaction.id}>
                            <td>{index + 1}</td>
                            <td>{formatDate(transaction.transaction_date)}</td>
                            <td>
                              {transaction.transaction_type === 'deposit' ? (
                                <span className="badge bg-success">ฝากเงิน</span>
                              ) : (
                                <span className="badge bg-danger">ถอนเงิน</span>
                              )}
                            </td>
                            <td className="text-right">{formatMoney(transaction.amount)}</td>
                            <td>{transaction.description || '-'}</td>
                            <td>{transaction.recorded_by_name}</td>
                            <td>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteTransaction(transaction.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-orange"
                  onClick={() => {
                    handleCloseHistoryModal();
                    handleShowTransactionModal(selectedStudent);
                  }}
                >
                  <i className="fas fa-plus-circle mr-1"></i> ทำรายการใหม่
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseHistoryModal}>
                  ปิด
                </button>
              </div>
            </div>
          </div>
          
          {/* พื้นหลังทึบ */}
          <div className="modal- fade show"></div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default TeacherSavings;